import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import DateField from './DateField';
import fscTariffsService, { TariffLine, TariffLinesResponse } from '../../services/fsctariffs.service';

/**
 * Tariff-lines sub-editor for a Fuel Surcharge Tariff (fueldata.fsc_tarifflines).
 * Mirrors V1 editfsctarifflines / savefsctarifflines: the line key is the decimal
 * Fuel Index (FUEL INDEX tariffs) or the Effective Date (WEEKLY/MONTHLY); the rate
 * shows a $ prefix for PER MILE and a % suffix for STANDARD. Removed lines are
 * hard-deleted on save (the unique tariff/date/index index forbids soft-delete).
 */
interface Props {
  tariffRecno: number;
  onClose: () => void;
  onSaved: () => void;
}

const s = (v: any): string => (v == null ? '' : String(v));
let _tmpId = -1;
const tmpId = () => _tmpId--;

const FscTariffLinesDrawer: React.FC<Props> = ({ tariffRecno, onClose, onSaved }) => {
  const [ctx, setCtx] = useState<TariffLinesResponse | null>(null);
  const [lines, setLines] = useState<TariffLine[]>([]);
  const [deletes, setDeletes] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);
  // Snapshot of the lines as loaded — the baseline for the unsaved-changes guard.
  const originalRef = useRef<TariffLine[]>([]);

  useEffect(() => {
    const ctrl = new AbortController();
    fscTariffsService.getLines(tariffRecno, ctrl.signal)
      .then((r) => { setCtx(r); setLines(r.lines); originalRef.current = r.lines; })
      .catch(() => {});
    return () => ctrl.abort();
  }, [tariffRecno]);

  const isIndex = ctx?.tariffType === 'FUEL INDEX';
  const rateType = ctx?.rateType ?? '';
  const readOnly = !!ctx?.expired;

  const setLine = (recno: number, patch: Partial<TariffLine>) =>
    setLines((p) => p.map((l) => (l.recno === recno ? { ...l, ...patch } : l)));

  // On these number inputs, ↑/↓ move focus to the same column in the adjacent
  // row (spreadsheet-style) instead of stepping the value. preventDefault fires
  // for every ↑/↓ so the native step never happens even at the first/last row.
  const handleArrowNav = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const cell = e.currentTarget.closest('td');
    const row = cell?.parentElement;
    if (!cell || !row) return;
    const col = Array.prototype.indexOf.call(row.children, cell);
    const sibling = e.key === 'ArrowDown' ? row.nextElementSibling : row.previousElementSibling;
    const target = sibling?.children[col]?.querySelector('input') as HTMLInputElement | null;
    if (target && !target.disabled) { target.focus(); target.select(); }
  };

  const addLine = () =>
    setLines((p) => [...p, { recno: tmpId(), fuelindex: '', effectivedate: '', fuelrate: '' }]);

  const removeLine = (line: TariffLine) => {
    if (line.recno > 0) setDeletes((d) => [...d, line.recno]);
    setLines((p) => p.filter((l) => l.recno !== line.recno));
  };

  // A line's key/rate are "filled" numerically: a FUEL INDEX of 0 (or a blank
  // date) and a rate of 0 count as empty, so a half-filled row is flagged inline
  // (rather than passing the client filter and being rejected by the server).
  const keyFilled = (l: TariffLine) =>
    isIndex ? parseFloat(s(l.fuelindex)) > 0 : s(l.effectivedate).trim() !== '';
  const rateFilled = (l: TariffLine) => parseFloat(s(l.fuelrate)) > 0;

  // A line's unique key — what the DB's tariff/date/index index enforces: the
  // numeric Fuel Index (folding 6 and 6.000 together) or the Effective Date.
  const lineKey = (l: TariffLine): string =>
    isIndex ? String(parseFloat(s(l.fuelindex)) || 0) : s(l.effectivedate).trim();

  // Duplicate keys among the filled lines — these would 1062 on save, so flag the
  // offending rows inline and block the save with a clear message.
  const dupes = useMemo(() => {
    const byKey = new Map<string, number[]>();
    lines.forEach((l) => {
      if (!keyFilled(l)) return;
      const k = lineKey(l);
      byKey.set(k, [...(byKey.get(k) ?? []), l.recno]);
    });
    const recnos = new Set<number>();
    const keys: string[] = [];
    byKey.forEach((rs, k) => { if (rs.length > 1) { rs.forEach((r) => recnos.add(r)); keys.push(k); } });
    return { recnos, keys };
  }, [lines, isIndex]);

  // Validate kept lines: key + rate are both-or-neither (rate > 0), and no two
  // lines share a key.
  const errors = useMemo(() => {
    const errs: string[] = [];
    if (lines.some((l) => keyFilled(l) !== rateFilled(l)))
      errs.push(`${isIndex ? 'Fuel Index' : 'Effective Date'} and Rate must both be filled (Rate > 0).`);
    if (dupes.keys.length)
      errs.push(`Duplicate ${isIndex ? 'Fuel Index' : 'Effective Date'}: ${dupes.keys.join(', ')} — each line must be unique.`);
    return errs;
  }, [lines, isIndex, dupes]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  // Unsaved-changes guard: warn before closing (Cancel / backdrop / Escape) when
  // any line was added, edited, or removed since load.
  const _norm = (arr: TariffLine[]) =>
    JSON.stringify(arr.map((l) => [l.recno, s(l.fuelindex), s(l.effectivedate), s(l.fuelrate)]));
  const isDirty = useMemo(
    () => deletes.length > 0 || _norm(lines) !== _norm(originalRef.current),
    [lines, deletes],
  );
  // Returns true to allow the close, false to veto it (drawer's onBeforeClose
  // contract). Shared by the footer Cancel button and the X / Esc / backdrop.
  const confirmDiscard = useCallback(
    () => !isDirty || window.confirm('You have unsaved tariff lines. Discard your changes?'),
    [isDirty],
  );
  const handleCancel = useCallback(() => { if (confirmDiscard()) onClose(); }, [confirmDiscard, onClose]);

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      const payload = lines
        // keep only complete rows (validation guarantees key/rate are both-or-neither),
        // so empty/half-blank trailing lines aren't sent
        .filter((l) => keyFilled(l) || rateFilled(l))
        .map((l) => ({
          recno: l.recno > 0 ? l.recno : 0,
          fuelindex: s(l.fuelindex),
          effectivedate: s(l.effectivedate),
          fuelrate: s(l.fuelrate),
        }));
      await fscTariffsService.saveLines(tariffRecno, { deletes, lines: payload });
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save tariff lines.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, lines, deletes, isIndex, tariffRecno, onClose, onSaved]);

  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={handleCancel} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      {!readOnly && (
        <button onClick={handleSave} disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
          {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
            : (<><CheckIcon className="w-4 h-4" /> Save Lines</>)}
        </button>
      )}
    </div>
  );

  const inputCls =
    'no-spinner w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm';

  return (
    <Drawer isOpen onClose={onClose} onBeforeClose={confirmDiscard} title="Add / Edit Tariff Lines" position="right" width="34rem" footer={footer}
      contentClassName="relative flex-1 px-4 sm:px-6 pb-6 overflow-y-auto">
      {/* Drop the scroll container's TOP padding (contentClassName above): with
          py-6 the sticky header pinned below the 24px padding and rows scrolled
          up through that strip above it. Top breathing room is re-added here
          (pt-6) so it scrolls away cleanly instead of leaving a gap. */}
      <div className="space-y-4 pt-6">
        <div ref={topRef} />
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}
        {readOnly && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 p-3 text-sm text-amber-800 dark:text-amber-300">
            This tariff has expired — its lines are read-only.
          </div>
        )}

        {/* Real table so the column headers align with the inputs and stay frozen
            (sticky thead) as the lines scroll — see RecordHistoryDrawer. */}
        <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
          <colgroup>
            <col style={{ width: '46%' }} />
            <col style={{ width: '46%' }} />
            <col style={{ width: '8%' }} />
          </colgroup>
          <thead>
            {/* Sticky goes on the <th> cells (not the <tr>) with border-separate:
                Chrome ignores `position: sticky` on a <tr> under border-collapse,
                which left the header lagging with a row floating above it. */}
            <tr className="text-left text-xs font-semibold text-muted">
              <th className="sticky top-0 z-10 bg-theme-bg-secondary py-2 pr-2 font-medium border-b border-theme-border-primary">{isIndex ? 'Fuel Index' : 'Effective Date'}</th>
              <th className="sticky top-0 z-10 bg-theme-bg-secondary py-2 px-2 font-medium border-b border-theme-border-primary">Rate</th>
              <th className="sticky top-0 z-10 bg-theme-bg-secondary py-2 font-medium border-b border-theme-border-primary text-center">{!readOnly && 'Del'}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const isDup = dupes.recnos.has(l.recno);
              const keyCls = isDup ? `${inputCls} !border-red-400 dark:!border-red-500` : inputCls;
              return (
                <tr key={l.recno}>
                  <td className="py-1.5 pr-2 align-middle">
                    {isIndex ? (
                      <input type="number" step="0.001" disabled={isSaving || readOnly} className={keyCls}
                        title={isDup ? 'Duplicate Fuel Index — must be unique' : undefined} onKeyDown={handleArrowNav}
                        value={s(l.fuelindex)} onChange={(e) => setLine(l.recno, { fuelindex: e.target.value })} />
                    ) : (
                      <DateField disabled={isSaving || readOnly} invalid={isDup}
                        value={s(l.effectivedate)} onChange={(v) => setLine(l.recno, { effectivedate: v })} />
                    )}
                  </td>
                  <td className="py-1.5 px-2 align-middle">
                    <div className="relative flex items-center">
                      {(rateType === 'PER MILE' || rateType === 'FLAT') && <span className="absolute left-2 text-gray-500">$</span>}
                      <input type="number" step="0.0001" disabled={isSaving || readOnly} onKeyDown={handleArrowNav}
                        className={`${inputCls} ${rateType === 'PER MILE' || rateType === 'FLAT' ? 'pl-5' : ''} ${rateType === 'STANDARD' ? 'pr-6' : ''}`}
                        value={s(l.fuelrate)} onChange={(e) => setLine(l.recno, { fuelrate: e.target.value })} />
                      {rateType === 'STANDARD' && <span className="absolute right-2 text-gray-500">%</span>}
                    </div>
                  </td>
                  <td className="py-1.5 align-middle text-center">
                    {!readOnly && (
                      <button type="button" onClick={() => removeLine(l)} disabled={isSaving}
                        className="p-1 text-red-500 hover:text-red-700" title="Remove line">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!readOnly && (
          <button type="button" onClick={addLine} disabled={isSaving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
            <PlusIcon className="w-4 h-4" /> Add Line
          </button>
        )}
      </div>
    </Drawer>
  );
};

export default FscTariffLinesDrawer;
